// Root router for the TWM admin app and embedded customer portal.
// Vercel redeploy nudge (post repo-reconnect): touch frontend/ to trip the
// path-scoped Ignored Build Step and force a fresh build.

import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { FluentProvider } from '@fluentui/react-components';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { DebugProvider, useDebug } from './contexts/DebugContext';
import './styles/app.css';
import Layout from './layout/Layout';
import Home from './pages/Home';
import Booking from './pages/Booking';
import Calendar from './pages/Calendar';
import Orders from './pages/Orders';
import OrderDetails from './pages/OrderDetails';
import ProjectDetails from './pages/ProjectDetails';
import Projects from './pages/Projects';
import Customers from './pages/Customers';
import CustomerProfile from './pages/CustomerProfile';
import ProductGroups from './pages/ProductGroups';
import ProductGroupDetails from './pages/ProductGroupDetails';
import Documents from './pages/Documents';
import DocumentEdit from './pages/DocumentEdit';
import CRM from './pages/CRM';
import Financial from './pages/Financial';
import Reports from './pages/Reports';
import Website from './pages/Website';
import Settings from './pages/Settings';
import Geofences from './pages/Geofences';
import CustomerMap from './pages/CustomerMap';
import CustomerVisits from './pages/CustomerVisits';
import LocationHistory from './pages/LocationHistory';
import MapOptions from './pages/MapOptions';
import Messaging from './pages/Messaging';
import ShareETA from './pages/ShareETA';
import Loadboard from './pages/Loadboard';
import SearchResults from './pages/SearchResults';
import ApiTools from './pages/ApiTools';
import DbTools from './pages/DbTools';
import DevSettings from './pages/DevSettings';
import OrderDialogPreview from './pages/OrderDialogPreview';
import EmailReceiptPreview from './pages/EmailReceiptPreview';
import PdfTemplatePreview from './pages/PdfTemplatePreview';
import PayLinkExerciser from './pages/PayLinkExerciser';
import SystemHealth from './pages/SystemHealth';
import OperationsMap from './pages/OperationsMap';
import Maintenance from './pages/Maintenance';
import Tasks from './pages/Tasks';
import Jobs from './pages/Jobs';
import ActivityFeed from './pages/ActivityFeed';
import Logs from './pages/Logs';
import ContactSubmissions from './pages/ContactSubmissions';
import Leads from './pages/Leads';
import Profile from './pages/Profile';
import Users from './pages/Users';

// Admin auth
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { ProxyRoleProvider, useProxyRole } from './contexts/ProxyRoleContext';
import AdminRoute from './components/AdminRoute';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { canAccessRoute } from './config/roleAccess';
import { isProd } from './config/appEnv';

// Customer Portal imports
import {
  PortalAuthProvider,
  PortalLayout,
  ProtectedRoute,
  Login as PortalLogin,
  Dashboard as PortalDashboard,
  Orders as PortalOrders,
  OrderDetails as PortalOrderDetails,
  LiveTracking as PortalLiveTracking,
  Documents as PortalDocuments,
  Messages as PortalMessages,
  Account as PortalAccount,
} from './portal';

function TanstackDevtools() {
  const { tanstackDevtools } = useDebug();
  return tanstackDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null;
}

function ThemedFluentProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  return <FluentProvider theme={theme}>{children}</FluentProvider>;
}

function OwnerOnly() {
  const { user } = useAdminAuth();
  if (!user?.owner) return <Navigate to="/" replace />;
  return <Outlet />;
}

// User-management guard: owner + admin only (matches the backend /employees
// gate). Honors the proxy-role override so simulated dispatcher/office/driver
// views can't reach /users either.
function UserAdminOnly() {
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();
  const effectiveRole = proxyRole ?? user?.role ?? 'office';
  if (effectiveRole !== 'owner' && effectiveRole !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// Route-level access guard. Bounces a role home when it hits a surface it may
// not reach — driver (dashboard only) and office (scoped to its allow-list for
// the soft launch). admin/owner/dispatcher keep full route access. Backed by the
// shared allow-list in config/roleAccess so nav visibility and route access can't
// drift apart. Effective role mirrors the nav: proxy override wins, else user.role.
function RequireRouteAccess() {
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();
  const { pathname } = useLocation();
  const effectiveRole = proxyRole ?? user?.role ?? 'office';
  if (!canAccessRoute(effectiveRole, pathname)) return <Navigate to="/" replace />;
  return <Outlet />;
}

function App() {
  return (
    <DebugProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <ThemedFluentProvider>
      <BrowserRouter>
        <AdminAuthProvider>
        <Routes>
          <Route path="/login" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Customer Portal Routes */}
          <Route path="/portal/login" element={
            <PortalAuthProvider>
              <PortalLogin />
            </PortalAuthProvider>
          } />
          <Route path="/portal" element={
            <PortalAuthProvider>
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            </PortalAuthProvider>
          }>
            <Route index element={<PortalDashboard />} />
            <Route path="orders" element={<PortalOrders />} />
            <Route path="orders/:orderId" element={<PortalOrderDetails />} />
            <Route path="tracking/:orderId" element={<PortalLiveTracking />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="account" element={<PortalAccount />} />
            <Route path="account/addresses" element={<PortalAccount />} />
            <Route path="account/payment" element={<PortalAccount />} />
            <Route path="account/security" element={<PortalAccount />} />
            <Route path="account/activity" element={<PortalAccount />} />
          </Route>

          {/* Admin App Routes */}
          <Route path="/" element={<AdminRoute><ProxyRoleProvider><Layout /></ProxyRoleProvider></AdminRoute>}>
            <Route index element={<Home />} />
            <Route path="home" element={<Home />} />
            {/* Driver = dashboard only; everything below redirects drivers to /. */}
            <Route element={<RequireRouteAccess />}>
            <Route path="profile" element={<Profile />} />
            <Route path="operations-map" element={<OperationsMap />} />
            <Route path="Geofences" element={<Geofences />} />
            <Route path="CustomerMap" element={<CustomerMap />} />
            <Route path="CustomerVisits" element={<CustomerVisits />} />
            <Route path="LocationHistory" element={<LocationHistory />} />
            <Route path="MapOptions" element={<MapOptions />} />
            <Route path="Messaging" element={<Messaging />} />
            <Route path="ShareETA" element={<ShareETA />} />
            <Route path="booking" element={<Booking />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="loadboard" element={<Loadboard />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="orders" element={<Orders />} />
            {/* Single route handles both /orders/new (orderId='new', isNewOrder=true)
                and /orders/:UUID. Unifying them lets the OrderDetails component
                instance persist across the draft-save navigation — no remount,
                no spinner flash. */}
            <Route path="orders/:orderId" element={<OrderDetails />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:projectId" element={<ProjectDetails />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:customerId" element={<CustomerProfile />} />
            {/* Staff user management — owner/admin only (UserAdminOnly + backend gate). */}
            <Route element={<UserAdminOnly />}>
              <Route path="users" element={<Users />} />
            </Route>
            <Route path="search" element={<SearchResults />} />
            <Route path="product_groups" element={<ProductGroups />} />
            <Route path="product_groups/:id/inventory" element={<ProductGroupDetails />} />
            <Route path="documents" element={<Documents />} />
            <Route path="documents/:documentId" element={<DocumentEdit />} />
            <Route path="crm" element={<CRM />} />
            <Route path="financial" element={<Financial />} />
            <Route path="reports" element={<Reports />} />
            <Route path="website" element={<Website />} />
            <Route path="settings" element={<Settings selected="general" />} />
            <Route path="settings/general" element={<Settings selected="general" />} />
            <Route path="settings/pricing" element={<Settings selected="pricing" />} />
            <Route path="settings/taxes" element={<Settings selected="taxes" />} />
            <Route path="settings/tier-map" element={<Settings selected="tier-map" />} />
            <Route path="settings/rental-period" element={<Settings selected="rental-period" />} />
            <Route path="settings/payments" element={<Settings selected="payments" />} />
            <Route path="settings/authnet-review" element={<Settings selected="authnet-review" />} />
            <Route path="settings/custom-fields" element={<Settings selected="custom-fields" />} />
            <Route path="settings/locations" element={<Settings selected="locations" />} />
            <Route path="settings/emails" element={<Settings selected="emails" />} />
            <Route path="settings/documents" element={<Settings selected="documents" />} />
            <Route path="settings/locale" element={<Settings selected="locale" />} />
            <Route path="settings/security" element={<Settings selected="security" />} />
            <Route path="settings/account" element={<Settings selected="account" />} />
            <Route path="settings/sale-items" element={<Settings selected="sale-items" />} />
            <Route path="settings/online-bookings" element={<Settings selected="online-bookings" />} />
            <Route path="settings/booking-page" element={<Settings selected="" />} />
            <Route path="settings/website-builder" element={<Settings selected="" />} />
            <Route path="settings/website-integration" element={<Settings selected="" />} />
            <Route path="settings/online-reservations" element={<Settings selected="" />} />
            <Route path="settings/pickup-and-delivery" element={<Settings selected="" />} />
            <Route path="settings/checkout" element={<Settings selected="" />} />
            <Route path="settings/seo" element={<Settings selected="" />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="activity" element={<ActivityFeed />} />
            <Route path="logs" element={<Logs />} />
            <Route path="contact-submissions" element={<ContactSubmissions />} />
            <Route path="leads" element={<Leads />} />
            {/* Dev tools — owner-only at runtime, available in every environment. */}
            <Route element={<OwnerOnly />}>
              <Route path="dev" element={<Navigate to="/dev/api-tools" replace />} />
              <Route path="dev/api-tools" element={<ApiTools />} />
              <Route path="dev/db-tools" element={<DbTools />} />
              <Route path="dev/system-health" element={<SystemHealth />} />
              <Route path="dev/settings" element={<DevSettings />} />
              <Route path="dev/order-dialog-preview" element={<OrderDialogPreview />} />
              <Route path="dev/email-receipt" element={<EmailReceiptPreview />} />
              {/* PDF preview relies on the website react-pdf dev route, which
                  is 403'd on the prod host — don't register it in prod. */}
              {!isProd() && <Route path="dev/pdf-templates" element={<PdfTemplatePreview />} />}
              <Route path="dev/pay-links" element={<PayLinkExerciser />} />
            </Route>
            </Route>
          </Route>
        </Routes>
        </AdminAuthProvider>
      </BrowserRouter>
      </ThemedFluentProvider>
      </ThemeProvider>
      <TanstackDevtools />
    </QueryClientProvider>
    </DebugProvider>
  );
}

export default App;
// chore: touch to force Vercel redeploy — verify org-move reconnection (2026-07-03)

// redeploy trigger 2 — force fresh build post org-move (2026-07-03b)
